/**
 * Candidate-side API client. All calls hit /api/candidate/* endpoints (Plan 03-02).
 *
 * DEDICATED candidateFetch — NOT the admin lib/api.ts apiFetch (Eva decision 2026-05-30,
 * Plan 03-05 pre-flight divergence #2): the admin apiFetch injects the admin JWT from
 * useAuthStore and hard-redirects to /login on any 401. The candidate surface is a public,
 * non-admin context with its OWN sessionStorage Bearer token; a 401 here must surface a
 * localized "session expired" + send the candidate back to the consent screen
 * (RequireCandidateSession), NEVER touch the admin store or bounce to /login. This mirrors
 * the backend's deliberate separation of the candidate-token auth subsystem from admin JWT (5.0h).
 *
 * Reuses ApiError from @/types/api (NOT @/lib/api — divergence #1) so mapErrorSlug works.
 */
import { ApiError } from "@/types/api";
import type { ApiErrorBody } from "@/types/api";

const CANDIDATE_SESSION_KEY = "candidate_session_token";

export function getCandidateSessionToken(): string | null {
  return sessionStorage.getItem(CANDIDATE_SESSION_KEY);
}

export function setCandidateSessionToken(token: string): void {
  sessionStorage.setItem(CANDIDATE_SESSION_KEY, token);
}

export function clearCandidateSessionToken(): void {
  sessionStorage.removeItem(CANDIDATE_SESSION_KEY);
}

// Plan 03-06 D-D/D-F: submit_assessment kills the Bearer token server-side, so the
// /done screen cannot re-fetch via /session. We persist the reference_number in
// sessionStorage on submit success so /done survives a hard refresh within the tab
// without any live-token dependency.
const CANDIDATE_REFERENCE_KEY = "candidate_reference_number";

export function getCandidateReferenceNumber(): string | null {
  return sessionStorage.getItem(CANDIDATE_REFERENCE_KEY);
}

export function setCandidateReferenceNumber(ref: string): void {
  sessionStorage.setItem(CANDIDATE_REFERENCE_KEY, ref);
}

/**
 * Candidate-scoped fetch. Attaches the sessionStorage Bearer token when present.
 * Throws ApiError on any non-2xx (no admin-store mutation, no /login redirect) so
 * callers route the error through mapErrorSlug + sonner toast + RequireCandidateSession.
 */
export async function candidateFetch<T>(
  path: string,
  opts: RequestInit = {},
): Promise<T> {
  const headers = new Headers(opts.headers);
  if (!headers.has("Content-Type") && opts.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }
  const tok = getCandidateSessionToken();
  if (tok) headers.set("Authorization", `Bearer ${tok}`);

  const res = await fetch(path, { ...opts, headers });

  if (!res.ok) {
    const body: ApiErrorBody = await res
      .json()
      .catch(() => ({ detail: "api_error" }));
    throw new ApiError(
      res.status,
      body,
      res.headers.get("x-request-id") ?? undefined,
    );
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ---- Response/request shapes (match backend/app/schemas/candidate.py EXACTLY, BUG-017) ----

export interface ConsentResponse {
  position_id: number;
  position_title: string;
  position_language: "es" | "en" | "de";
  deadline: string;
  consent_text_md: string;
  consent_version_id: number;
  consent_version_number: number;
  // Plan 05-08/09 (D1) — responsable identity header, served from ComplianceConfig.
  company_legal_name: string | null;
  cedula_juridica: string | null;
  address: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  prodhab_number: string | null;
  data_protection_contact: string | null;
  data_recipients: string | null;
}

export interface ConsentAcceptedResponse {
  candidate_id: number;
  session_token: string;
  next_url: string;
}

export interface PersonalInfoPayload {
  first_name: string;
  primer_apellido: string;
  segundo_apellido: string;
  email: string;
  phone: string;
  salary_expectation: number;
  salary_currency: "CRC" | "USD";
  earliest_start_date: string; // YYYY-MM-DD
}

export interface PersonalInfoSaved {
  next_url: string;
}

export interface DraftAnswerSummary {
  position_question_id: number;
  question_type: "multiple_choice" | "short_answer";
  choice_id: string | null;
  short_answer_text: string | null;
  elapsed_seconds: number;
}

export interface SessionStateResponse {
  candidate_id: number;
  stage_1_status:
    | "consent_only"
    | "in_progress"
    | "submitted"
    | "advanced_to_stage_2"
    | "rejected";
  last_question_index: number;
  drafts: DraftAnswerSummary[];
  tab_switch_count: number;
  started_at: string | null;
  position_id: number;
  position_language: "es" | "en" | "de";
  total_questions: number;
  reference_number: string | null; // Plan 03-06 D-D: /done refresh survival
}

// ---- Plan 03-06 shapes (match backend/app/schemas/candidate.py EXACTLY) ----

/** GET /api/candidate/questions item — snapshot with correct_answer + competency stripped (CAN-04). */
export interface CandidateQuestion {
  position_question_id: number;
  display_order: number;
  question_type: "multiple_choice" | "short_answer";
  text: string;
  options: Record<string, string> | null;
}

/** POST /api/candidate/answers/draft body (DraftAnswerRequest). */
export interface DraftAnswerPayload {
  position_question_id: number;
  question_type: "multiple_choice" | "short_answer";
  choice_id?: string | null;
  short_answer_text?: string | null;
  elapsed_seconds: number;
}

export interface DraftAccepted {
  question_id: number;
  persisted_at: string;
}

/** POST /api/candidate/tab-switch body (TabSwitchRequest). */
export interface TabSwitchPayload {
  occurred_at: string;
  question_id: number | null;
  event_type: "blur" | "focus";
}

export interface TabSwitchAccepted {
  event_id: number;
}

export interface SubmitResponse {
  reference_number: string;
  submitted_at: string;
  next_url: string;
}

// ---- Endpoints (Plan 03-02 backend) ----

export async function fetchConsent(positionToken: string): Promise<ConsentResponse> {
  return candidateFetch<ConsentResponse>(
    `/api/candidate/consent/${positionToken}`,
  );
}

export async function postConsent(
  positionToken: string,
  consentVersionId: number,
): Promise<ConsentAcceptedResponse> {
  return candidateFetch<ConsentAcceptedResponse>("/api/candidate/consent", {
    method: "POST",
    body: JSON.stringify({
      position_token: positionToken,
      consent_version_id: consentVersionId,
      accepted: true,
    }),
  });
}

export async function postPersonalInfo(
  payload: PersonalInfoPayload,
): Promise<PersonalInfoSaved> {
  return candidateFetch<PersonalInfoSaved>("/api/candidate/personal-info", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function fetchSessionState(): Promise<SessionStateResponse> {
  return candidateFetch<SessionStateResponse>("/api/candidate/session");
}

// ---- Endpoints (Plan 03-06 backend) ----

/** GET the candidate's ordered question list (Plan 03-06 D-A). Immutable post-Activate → fetch once. */
export async function fetchQuestions(): Promise<CandidateQuestion[]> {
  return candidateFetch<CandidateQuestion[]>("/api/candidate/questions");
}

/** UPSERT a draft answer (D1 + D10). Returns the persisted-at stamp. */
export async function postDraft(
  payload: DraftAnswerPayload,
): Promise<DraftAccepted> {
  return candidateFetch<DraftAccepted>("/api/candidate/answers/draft", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/**
 * Fire-and-forget tab-switch record (D4 passive recording). Resolves silently on
 * any failure — an assessment must NEVER break because a visibilitychange POST
 * failed on a weak mobile signal (over-report bias is safer than crash).
 */
export async function postTabSwitch(payload: TabSwitchPayload): Promise<void> {
  try {
    await candidateFetch<TabSwitchAccepted>("/api/candidate/tab-switch", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  } catch {
    // swallow per D4 — no surface, no retry, no user-visible failure
  }
}

/** Finalize Stage 1 (D5). Server reads LATEST drafts; idempotent on second call. */
export async function postSubmit(): Promise<SubmitResponse> {
  return candidateFetch<SubmitResponse>("/api/candidate/submit", {
    method: "POST",
    body: JSON.stringify({}),
  });
}
